'use client';

import { useEffect, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import {
  sendBrowserNotification,
  registerServiceWorker,
  requestNotificationPermission,
} from '@/lib/pushNotifications';
import { useToast } from '@/context/ToastContext';

/**
 * useRealtimeNotifications
 *
 * Sets up Supabase real-time subscriptions for:
 *  - New matches (matches table INSERT)
 *  - New messages (messages table INSERT, not sent by current user)
 *  - Profile views (profile_views table INSERT, viewed_user_id = current user)
 *
 * Fires both browser push notifications AND in-app toast notifications.
 */
export function useRealtimeNotifications(userId: string | null) {
  const channelRef = useRef<ReturnType<NonNullable<ReturnType<typeof createClient>>['channel']> | null>(null);
  const userIdRef = useRef<string | null>(userId);
  const { addToast } = useToast();
  const addToastRef = useRef(addToast);

  // Keep refs in sync
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    addToastRef.current = addToast;
  }, [addToast]);

  useEffect(() => {
    registerServiceWorker();
    requestNotificationPermission();
  }, []);

  useEffect(() => {
    if (!userId) return;

    const supabase = createClient();
    if (!supabase) return;

    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const channel = supabase
      .channel(`realtime_notifications_${userId}`)

      // ── New Match ──────────────────────────────────────────────────────────
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'matches' },
        async (payload: any) => {
          const newMatch = payload.new as any;
          const currentId = userIdRef.current;
          if (!currentId) return;

          if (newMatch.user1_id !== currentId && newMatch.user2_id !== currentId) return;

          const otherId = newMatch.user1_id === currentId ? newMatch.user2_id : newMatch.user1_id;

          try {
            const { data: profile } = await supabase
              .from('user_profiles')
              .select('display_name')
              .eq('id', otherId)
              .maybeSingle();
            const name = profile?.display_name || 'Someone';
            sendBrowserNotification('match', name);
            addToastRef.current({
              type: 'match',
              title: name,
              message: "You and " + name + " liked each other!",
            });
          } catch {
            sendBrowserNotification('match', 'Someone');
            addToastRef.current({
              type: 'match',
              title: 'Someone',
              message: 'You have a new match!',
            });
          }
        }
      )

      // ── New Message ────────────────────────────────────────────────────────
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        async (payload: any) => {
          const newMsg = payload.new as any;
          const currentId = userIdRef.current;
          if (!currentId) return;

          if (newMsg.sender_id === currentId) return;
          if (newMsg.receiver_id && newMsg.receiver_id !== currentId) return;

          try {
            const { data: profile } = await supabase
              .from('user_profiles')
              .select('display_name')
              .eq('id', newMsg.sender_id)
              .maybeSingle();
            const name = profile?.display_name || 'Someone';
            sendBrowserNotification('message', name, newMsg.content);
            addToastRef.current({
              type: 'message',
              title: name,
              message: newMsg.content || 'Sent you a message',
            });
          } catch {
            sendBrowserNotification('message', 'Someone');
            addToastRef.current({
              type: 'message',
              title: 'Someone',
              message: 'Sent you a message',
            });
          }
        }
      )

      // ── Profile View ───────────────────────────────────────────────────────
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'profile_views',
          filter: `viewed_user_id=eq.${userId}`,
        },
        async (payload: any) => {
          const view = payload.new as any;
          const viewerId = view.viewer_id;
          if (!viewerId) return;

          try {
            const { data: profile } = await supabase
              .from('user_profiles')
              .select('display_name')
              .eq('id', viewerId)
              .maybeSingle();
            const name = profile?.display_name || 'Someone';
            sendBrowserNotification('profile_view', name);
            addToastRef.current({
              type: 'profile_view',
              title: name,
              message: name + ' viewed your profile',
            });
          } catch {
            sendBrowserNotification('profile_view', 'Someone');
            addToastRef.current({
              type: 'profile_view',
              title: 'Someone',
              message: 'Someone viewed your profile',
            });
          }
        }
      )

      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [userId]);
}
